import type { StorageConnector, StorageConnectorId } from './types'

const DB_NAME = 'byokapp-storage'
const DB_VERSION = 1
const HANDLES_STORE = 'handles'
const HANDLE_KEY = 'localFolder'

/** Persistence interface for the directory handle — injectable for testing. */
export interface HandleStorage {
  load(): Promise<{ name: string; handle: FileSystemDirectoryHandle } | null>
  save(name: string, handle: FileSystemDirectoryHandle): Promise<void>
  delete(): Promise<void>
}

/** Default IDB-backed HandleStorage — uses File System Access serialisation. */
class IdbHandleStorage implements HandleStorage {
  private readonly idbFactory: IDBFactory

  constructor(idbFactory: IDBFactory) {
    this.idbFactory = idbFactory
  }

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = this.idbFactory.open(DB_NAME, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(HANDLES_STORE)) {
          db.createObjectStore(HANDLES_STORE, { keyPath: 'key' })
        }
        if (!db.objectStoreNames.contains('credentials')) {
          db.createObjectStore('credentials', { keyPath: 'id' })
        }
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  async load(): Promise<{ name: string; handle: FileSystemDirectoryHandle } | null> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(HANDLES_STORE, 'readonly')
      const req = tx.objectStore(HANDLES_STORE).get(HANDLE_KEY)
      req.onsuccess = () => {
        const rec = req.result as
          | { key: string; name: string; handle: FileSystemDirectoryHandle }
          | undefined
        resolve(rec ? { name: rec.name, handle: rec.handle } : null)
        db.close()
      }
      req.onerror = () => {
        reject(req.error)
        db.close()
      }
    })
  }

  async save(name: string, handle: FileSystemDirectoryHandle): Promise<void> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(HANDLES_STORE, 'readwrite')
      const req = tx.objectStore(HANDLES_STORE).put({ key: HANDLE_KEY, name, handle })
      req.onsuccess = () => {
        resolve()
        db.close()
      }
      req.onerror = () => {
        reject(req.error)
        db.close()
      }
    })
  }

  async delete(): Promise<void> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(HANDLES_STORE, 'readwrite')
      const req = tx.objectStore(HANDLES_STORE).delete(HANDLE_KEY)
      req.onsuccess = () => {
        resolve()
        db.close()
      }
      req.onerror = () => {
        reject(req.error)
        db.close()
      }
    })
  }
}

/** Map-backed HandleStorage for tests — no structured-clone constraint. */
export class MapHandleStorage implements HandleStorage {
  private store: Map<string, { name: string; handle: FileSystemDirectoryHandle }> = new Map()

  async load() {
    return this.store.get(HANDLE_KEY) ?? null
  }
  async save(name: string, handle: FileSystemDirectoryHandle) {
    this.store.set(HANDLE_KEY, { name, handle })
  }
  async delete() {
    this.store.delete(HANDLE_KEY)
  }
}

interface LocalFolderOptions {
  handleStorage?: HandleStorage
  idbFactory?: IDBFactory
  picker?: () => Promise<FileSystemDirectoryHandle>
}

export class LocalFolderConnector implements StorageConnector {
  readonly id: StorageConnectorId = 'local-folder'

  private handle: FileSystemDirectoryHandle | null = null
  private _folderName: string | null = null
  private _permissionLost = false

  private readonly handleStorage: HandleStorage
  // Lazily resolved so the class is safe to import in Node/test environments
  private readonly _pickerOpt: (() => Promise<FileSystemDirectoryHandle>) | undefined

  private get picker(): () => Promise<FileSystemDirectoryHandle> {
    return this._pickerOpt ?? (() => window.showDirectoryPicker({ mode: 'readwrite' }))
  }

  constructor(options: LocalFolderOptions = {}) {
    const idbFactory = options.idbFactory
    this.handleStorage =
      options.handleStorage ??
      new IdbHandleStorage(
        idbFactory ??
          (typeof indexedDB !== 'undefined' ? indexedDB : (null as unknown as IDBFactory)),
      )
    this._pickerOpt = options.picker
  }

  isConfigured(): boolean {
    return this.handle !== null
  }

  get folderName(): string | null {
    return this._folderName
  }

  get permissionLost(): boolean {
    return this._permissionLost
  }

  async init(): Promise<void> {
    const stored = await this.handleStorage.load()
    if (!stored) return

    this._folderName = stored.name
    const perm = await stored.handle.queryPermission({ mode: 'readwrite' })
    if (perm === 'granted') {
      this.handle = stored.handle
      this._permissionLost = false
    } else {
      this.handle = null
      this._permissionLost = true
    }
  }

  async pick(): Promise<void> {
    const handle = await this.picker()
    this.handle = handle
    this._folderName = handle.name
    this._permissionLost = false
    await this.handleStorage.save(handle.name, handle)
  }

  async save(blob: Blob, filename: string): Promise<void> {
    if (!this.handle) throw new Error('No folder configured')

    const perm = await this.handle.requestPermission({ mode: 'readwrite' })
    if (perm !== 'granted') throw new Error('Folder permission denied')

    const fileHandle = await this.handle.getFileHandle(filename, { create: true })
    const writable = await fileHandle.createWritable()
    await writable.write(blob)
    await writable.close()
  }

  async disconnect(): Promise<void> {
    this.handle = null
    this._folderName = null
    this._permissionLost = false
    await this.handleStorage.delete()
  }
}
